/* eslint-disable import/no-unresolved */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Error,
  Notifications as NotificationsIcon, NotificationsActive, NotificationsNone, SyncProblem, VolumeOff, Warning,
} from '@mui/icons-material';
import {
  Badge, Box, Divider, Drawer, IconButton, Skeleton, Tooltip, Typography, useTheme,
} from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';
import API from 'src/utils/apiConfig';
import { useSocket } from 'src/utils/WsContext';
import { useAuth } from 'src/utils/AuthContext';
import { useApp } from 'src/utils/AppContext';

const Notifications = () => {
  const { socket, socketConnected } = useSocket();
  const { sessionInfo } = useAuth();
  const { assetsConfig } = useApp();
  const [freshNotifCount, setFreshNotifCount] = useState({ count: 0, lastNonFresh: Date.now() });
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const [notificationDataFlag, setNotificationDataFlag] = useState();
  const navigate = useNavigate();
  const audio = useRef(new Audio('/buzzer.mp3'));
  const [buzzerOn, setBuzzerOn] = useState(false);

  const handlePlay = async (play) => {
    if (play) {
      try {
        await audio.current.play();
        setBuzzerOn(true);
        // setPlayError('');
      } catch (err) {
        // setPlayError(err.message);
        setBuzzerOn(false);
        console.log(err);
      }
    } else {
      audio.current.pause();
      audio.current.load(); // reloads
      setBuzzerOn(false);
    }
  };

  useEffect(() => {
    if (!notificationDataFlag) return;
    if (open) setNotifications((prev) => [notificationDataFlag, ...prev]);
    else setFreshNotifCount((prev) => ({ ...prev, count: prev.count + 1 }));
    if (assetsConfig?.asset?.buzzer && notificationDataFlag.link?.includes('assets') && notificationDataFlag.level === 'violation') handlePlay(true);
  }, [notificationDataFlag]);

  useEffect(() => {
    if (!socket) return undefined;
    socket.emit('join-room', { name: sessionInfo.idToken.payload['cognito:username'] }, (r) => {
      if (r.success) console.log('joined user room');
    });
    socket.on('notification', (data) => setNotificationDataFlag(data));

    // Global toast listeners for bulk reports
    const onBulkReportComplete = (data) => {
      toast.success(data.message || 'Bulk report generated successfully.', { autoClose: 5000 });
    };
    const onBulkReportFailed = (data) => {
      toast.error(`Bulk report failed: ${data.error}`, { autoClose: 5000 });
    };
    const onBulkReportCancelled = (data) => {
      const currentUsername = sessionInfo?.idToken?.payload?.['cognito:username'];
      const currentEmail = sessionInfo?.idToken?.payload?.email;

      if (data?.createdBy && ![currentUsername, currentEmail].includes(data.createdBy)) return;

      toast.info(data.message || 'Bulk report generation cancelled.', { autoClose: 5000 });
    };

    socket.on('bulkReportComplete', onBulkReportComplete);
    socket.on('bulkReportFailed', onBulkReportFailed);
    socket.on('bulkReportCancelled', onBulkReportCancelled);

    return () => {
      socket.off('notification');
      socket.off('bulkReportComplete', onBulkReportComplete);
      socket.off('bulkReportFailed', onBulkReportFailed);
      socket.off('bulkReportCancelled', onBulkReportCancelled);
    };
  }, [socket, sessionInfo]);

  const getFreshNotifCount = async () => {
    try {
      const r = await API.get('/v1/notif/fresh');
      if (r.data.success) {
        setFreshNotifCount(r.data.data || { count: 0, lastNonFresh: Date.now() });
      } else throw new Error(r.data.message);
    } catch (err) {
      console.log('getFreshNotifCount::', err.message, err.code);
      toast.error(`Error getting getFreshNotifCount: ${err.response?.data?.message || err.response?.data || err.message}`);
    }
  };

  useEffect(() => {
    audio.current.loop = true;
    getFreshNotifCount();
  }, []);

  const getNotifications = async () => {
    setLoading(true);
    try {
      const r = await API.get('/v1/notif');
      if (r.data.success) {
        setNotifications(r.data.data);
        if (r.data.data.length) {
          setFreshNotifCount((prev) => ({
            count: 0,
            lastNonFresh: r.data.data?.[0].timestamp || prev.timestamp,
          }));
        }
      } else throw new Error(r.data.message);
    } catch (err) {
      console.log('getNotifications:: error:', err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) getNotifications();
    else setNotifications([]);
  }, [open]);

  const markAsReadOnServer = async ({ id }) => {
    const r = await API.patch('/v1/notif/read', { id });
    if (r.data.success) return true;
    throw new Error(r.data.message);
  };

  const handleClick = async (notif, index) => {
    navigate(notif.link.split('/')[3]);
    if (!notif.read) {
      const marked = await markAsReadOnServer(notif);
      if (marked) {
        setNotifications((prev) => {
          prev[index] = { ...notif, read: true }; // eslint-disable-line no-param-reassign
          return [...prev];
        });
      }
    }
    setOpen(false);
  };

  return (
    <>
      <IconButton
        sx={{
          position: 'fixed',
          top: '0.5rem',
          right: '0.5rem',
          background: 'white',
          boxShadow: 15,
          zIndex: 10,
          color: theme.palette.primary.main,
          ':hover': {
            background: theme.palette.primary.lighter,
          },
        }}
        onClick={() => { setOpen(true); }}
      >
        {socketConnected ? (
          <Badge max={99} badgeContent={socketConnected ? freshNotifCount.count : '!'} color="error">
            {buzzerOn ? <NotificationsActive /> : ( // eslint-disable-line no-nested-ternary
              freshNotifCount.count ? <NotificationsIcon /> : <NotificationsNone />)}
          </Badge>
        ) : (
          <SyncProblem color="error" />
        )}
      </IconButton>
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        anchor="right"
        PaperProps={{ sx: { width: 450 } }}
      >
        <Box sx={{
          display: 'flex', m: 1, alignItems: 'center', justifyContent: 'space-between',
        }}
        >
          <Typography variant="h6">Notifications</Typography>
          {assetsConfig?.asset?.buzzer && (
            <Tooltip title="Mute buzzer" placement="left">
              <span>
                <IconButton disabled={!buzzerOn} onClick={() => handlePlay(false)}>
                  <VolumeOff color={buzzerOn ? 'primary' : 'disabled'} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          {/* eslint-disable-next-line no-nested-ternary */}
          {loading ? (
            <>
              <Skeleton variant="rounded" height="7rem" sx={{ m: 1 }} />
              <Skeleton variant="rounded" height="7rem" sx={{ m: 1 }} />
              <Skeleton variant="rounded" height="7rem" sx={{ m: 1 }} />
            </>
          ) : (
            notifications.length ? notifications.map((notif, index) => (
              <React.Fragment key={notif.id}>
                <Divider />
                <Box
                  sx={{
                    padding: '0.5rem',
                    background: !notif.read && theme.palette.primary.lighter,
                    cursor: 'pointer',
                    ':hover': { background: theme.palette.primary.light },
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={() => handleClick(notif, index)}
                >
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                    {notif.level === 'violation' ? <Error fontSize="large" color="error" /> : <Warning fontSize="large" color="warning" />}
                  </Box>
                  <Box item xs={9} sx={{ flex: 7 }}>
                    <Typography fontWeight="bold">
                      {notif.title}
                    </Typography>
                    <Typography variant="body2">
                      {notif.description}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Typography variant="bodySmall">{moment.unix(Math.round(notif.timestamp / 1000)).fromNow()}</Typography>
                    </Box>
                  </Box>
                </Box>
              </React.Fragment>
            )) : (
              <>
                <Divider />
                <Typography variant="body2" m="1rem">No notifications to show.</Typography>
              </>
            )
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Notifications;
